import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { apiCall, ApiError } from '../utils/apiHelpers';
import { StatusBadge } from './SchemaListPage';
import type {
  AiScanResponse,
  ComplianceFinding,
  ComplianceFrameworkCode,
  EncodingMode,
  FrameworkConfig,
  FrameworkRuleConfig,
  FrameworksResponse,
  IntakeTokenResponse,
  PropertyType,
  RetentionPolicy,
  SchemaDoc,
  SchemaProperty,
  SensitiveLevel,
  TestCodeResponse,
} from '../types';

const PROPERTY_TYPES: PropertyType[] = ['text', 'number', 'date', 'select', 'boolean'];
const SENSITIVE_LEVELS: SensitiveLevel[] = ['none', 'low', 'high'];

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '0.5rem',
  border: '1px solid var(--border-color)',
  borderRadius: 4,
  background: 'var(--card-bg)',
  color: 'var(--text-color)',
};

const labelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: 13,
  fontWeight: 500,
  marginBottom: 4,
};

const sectionStyle: React.CSSProperties = {
  border: '1px solid var(--border-color)',
  background: 'var(--card-bg)',
  padding: 20,
  borderRadius: 8,
  marginBottom: 16,
};

const SNAKE_CASE = /^[a-z][a-z0-9_]*$/;

function blankProperty(order: number): SchemaProperty {
  return {
    name: '',
    label: '',
    type: 'text',
    pii: false,
    sensitiveLevel: 'none',
    required: false,
    options: null,
    order,
  };
}

function inlineDisallowed(properties: SchemaProperty[]): string | null {
  const piiField = properties.find((p) => p.pii);
  if (piiField) return `Inline encoding is not allowed: "${piiField.name || '(unnamed)'}" is marked PII.`;
  const highField = properties.find((p) => p.sensitiveLevel === 'high');
  if (highField)
    return `Inline encoding is not allowed: "${highField.name || '(unnamed)'}" is marked high sensitivity.`;
  return null;
}

function latestFindings(schema: SchemaDoc | null, source: 'rules_engine' | 'ai_scan'): ComplianceFinding[] {
  if (!schema) return [];
  const matching = schema.complianceReviews.filter((r) => r.source === source);
  const last = matching[matching.length - 1];
  return last?.results ?? [];
}

export function SchemaEditorPage() {
  const { industryId, schemaId } = useParams<{ industryId: string; schemaId?: string }>();
  const navigate = useNavigate();
  const isNew = !schemaId || schemaId === 'new';

  const [schema, setSchema] = useState<SchemaDoc | null>(null);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [aiFindings, setAiFindings] = useState<ComplianceFinding[]>([]);

  // Bootstrap form state (used only for new-schema mode).
  const [newName, setNewName] = useState('');
  const [newDescription, setNewDescription] = useState('');
  const [creating, setCreating] = useState(false);

  // Framework catalog (used to render chips, descriptions).
  const [frameworks, setFrameworks] = useState<FrameworkConfig[]>([]);
  const [rules, setRules] = useState<FrameworkRuleConfig[]>([]);
  const [intakeMaxPerDay, setIntakeMaxPerDay] = useState<number>(5000);

  // Modals.
  const [showRevokeModal, setShowRevokeModal] = useState(false);
  const [revokeReason, setRevokeReason] = useState('');
  const [showTestModal, setShowTestModal] = useState(false);
  const [showIntakeUrlModal, setShowIntakeUrlModal] = useState<string | null>(null);

  // Pending change tracker (so we can show "Saving..." indicator).
  const saveTimer = useRef<number | null>(null);

  // Load frameworks catalog once.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const fw = await apiCall<FrameworksResponse>('/api/compliance/frameworks');
        if (cancelled) return;
        setFrameworks(fw.frameworks);
        setRules(fw.rules);
        if (typeof fw.intakeSubmissionsPerDayMax === 'number') {
          setIntakeMaxPerDay(fw.intakeSubmissionsPerDayMax);
        }
      } catch {
        // catalog is best-effort; UI still functions if it fails
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Load schema in edit mode.
  useEffect(() => {
    if (isNew || !industryId || !schemaId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const s = await apiCall<SchemaDoc>(
          `/api/schemas/${schemaId}?industryId=${encodeURIComponent(industryId)}`,
          { skipCache: true },
        );
        if (cancelled) return;
        setSchema(s);
      } catch (e) {
        if (!cancelled) setError(e instanceof ApiError ? e.message : (e as Error).message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [industryId, schemaId, isNew]);

  // Debounced patch — only fires in edit mode.
  const queuePatch = useCallback(
    (patch: Partial<SchemaDoc>) => {
      if (!industryId || !schemaId || isNew) return;
      if (saveTimer.current) {
        window.clearTimeout(saveTimer.current);
      }
      saveTimer.current = window.setTimeout(async () => {
        setSaving(true);
        setError(null);
        try {
          const updated = await apiCall<SchemaDoc>(
            `/api/schemas/${schemaId}?industryId=${encodeURIComponent(industryId)}`,
            { method: 'PATCH', body: patch },
          );
          setSchema(updated);
        } catch (e) {
          setError(e instanceof ApiError ? e.message : (e as Error).message);
        } finally {
          setSaving(false);
        }
      }, 500);
    },
    [industryId, schemaId, isNew],
  );

  // Create a new schema (transitions from new-mode to edit-mode).
  async function createSchema(ev: React.FormEvent) {
    ev.preventDefault();
    if (!industryId || !newName.trim()) return;
    setCreating(true);
    setError(null);
    try {
      const created = await apiCall<SchemaDoc>('/api/schemas', {
        method: 'POST',
        body: {
          industryId,
          name: newName.trim(),
          description: newDescription.trim() || undefined,
        },
      });
      navigate(`/industries/${industryId}/schemas/${created.id}`);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : (e as Error).message);
    } finally {
      setCreating(false);
    }
  }

  async function publish() {
    if (!industryId || !schemaId) return;
    setSaving(true);
    setError(null);
    try {
      const updated = await apiCall<SchemaDoc>(
        `/api/schemas/${schemaId}/publish?industryId=${encodeURIComponent(industryId)}`,
        { method: 'POST' },
      );
      setSchema(updated);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : (e as Error).message);
    } finally {
      setSaving(false);
    }
  }

  async function archive() {
    if (!industryId || !schemaId) return;
    setSaving(true);
    setError(null);
    try {
      const updated = await apiCall<SchemaDoc>(
        `/api/schemas/${schemaId}/archive?industryId=${encodeURIComponent(industryId)}`,
        { method: 'POST' },
      );
      setSchema(updated);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : (e as Error).message);
    } finally {
      setSaving(false);
    }
  }

  async function confirmRevoke() {
    if (!industryId || !schemaId || !revokeReason.trim()) return;
    setSaving(true);
    setError(null);
    try {
      const updated = await apiCall<SchemaDoc>(
        `/api/schemas/${schemaId}/revoke?industryId=${encodeURIComponent(industryId)}`,
        { method: 'POST', body: { reason: revokeReason.trim() } },
      );
      setSchema(updated);
      setShowRevokeModal(false);
      setRevokeReason('');
    } catch (e) {
      setError(e instanceof ApiError ? e.message : (e as Error).message);
    } finally {
      setSaving(false);
    }
  }

  async function runAiScan() {
    if (!industryId || !schemaId) return;
    setSaving(true);
    setError(null);
    try {
      const res = await apiCall<AiScanResponse>(
        `/api/schemas/${schemaId}/ai-scan?industryId=${encodeURIComponent(industryId)}`,
        { method: 'POST' },
      );
      setAiFindings(res.findings);
      // Refresh schema so persisted complianceReviews include the new AI scan.
      const s = await apiCall<SchemaDoc>(
        `/api/schemas/${schemaId}?industryId=${encodeURIComponent(industryId)}`,
        { skipCache: true },
      );
      setSchema(s);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : (e as Error).message);
    } finally {
      setSaving(false);
    }
  }

  async function createOrRotateIntakeToken() {
    if (!industryId || !schemaId) return;
    setSaving(true);
    setError(null);
    try {
      const res = await apiCall<IntakeTokenResponse>(
        `/api/schemas/${schemaId}/intake-token?industryId=${encodeURIComponent(industryId)}`,
        { method: 'POST' },
      );
      setShowIntakeUrlModal(res.intakeUrl);
      const s = await apiCall<SchemaDoc>(
        `/api/schemas/${schemaId}?industryId=${encodeURIComponent(industryId)}`,
        { skipCache: true },
      );
      setSchema(s);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : (e as Error).message);
    } finally {
      setSaving(false);
    }
  }

  async function revokeIntakeToken() {
    if (!industryId || !schemaId) return;
    setSaving(true);
    setError(null);
    try {
      const updated = await apiCall<SchemaDoc>(
        `/api/schemas/${schemaId}/intake-token/revoke?industryId=${encodeURIComponent(industryId)}`,
        { method: 'POST' },
      );
      setSchema(updated);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : (e as Error).message);
    } finally {
      setSaving(false);
    }
  }

  // -------------------- Local edits proxy through queuePatch --------------------

  function patchSchemaLocally(updater: (s: SchemaDoc) => SchemaDoc, patch: Partial<SchemaDoc>) {
    setSchema((prev) => (prev ? updater(prev) : prev));
    queuePatch(patch);
  }

  function updateName(value: string) {
    patchSchemaLocally((s) => ({ ...s, name: value }), { name: value });
  }
  function updateDescription(value: string) {
    patchSchemaLocally((s) => ({ ...s, description: value }), { description: value });
  }
  function updateProperty(index: number, patch: Partial<SchemaProperty>) {
    if (!schema) return;
    const next = schema.properties.map((p, i) => (i === index ? { ...p, ...patch } : p));
    patchSchemaLocally((s) => ({ ...s, properties: next }), { properties: next });
  }
  function addProperty() {
    if (!schema) return;
    const next: SchemaProperty[] = [...schema.properties, blankProperty(schema.properties.length)];
    patchSchemaLocally((s) => ({ ...s, properties: next }), { properties: next });
  }
  function removeProperty(index: number) {
    if (!schema) return;
    const removed = schema.properties[index];
    const next = schema.properties
      .filter((_, i) => i !== index)
      .map((p, i) => ({ ...p, order: i }));
    const primaryCleared =
      schema.primaryIdentifierField === removed?.name ? null : schema.primaryIdentifierField;
    patchSchemaLocally(
      (s) => ({ ...s, properties: next, primaryIdentifierField: primaryCleared }),
      { properties: next, primaryIdentifierField: primaryCleared },
    );
  }
  function moveProperty(index: number, direction: -1 | 1) {
    if (!schema) return;
    const target = index + direction;
    if (target < 0 || target >= schema.properties.length) return;
    const next = [...schema.properties];
    const a = next[index];
    const b = next[target];
    if (!a || !b) return;
    next[index] = b;
    next[target] = a;
    const reordered = next.map((p, i) => ({ ...p, order: i }));
    patchSchemaLocally((s) => ({ ...s, properties: reordered }), { properties: reordered });
  }
  function updateEncodingMode(mode: EncodingMode) {
    patchSchemaLocally((s) => ({ ...s, encodingMode: mode }), { encodingMode: mode });
  }
  function updatePrimaryIdentifier(value: string) {
    const v = value || null;
    patchSchemaLocally((s) => ({ ...s, primaryIdentifierField: v }), { primaryIdentifierField: v });
  }
  function updateFrameworks(next: ComplianceFrameworkCode[]) {
    patchSchemaLocally((s) => ({ ...s, complianceFrameworks: next }), { complianceFrameworks: next });
  }
  function updateRetention(next: RetentionPolicy | null) {
    patchSchemaLocally((s) => ({ ...s, retentionPolicy: next }), { retentionPolicy: next });
  }
  function updateSelfIntakeEnabled(enabled: boolean) {
    if (!schema) return;
    const next = { ...schema.selfIntake, enabled };
    patchSchemaLocally((s) => ({ ...s, selfIntake: next }), { selfIntake: next });
  }
  function updateSelfIntakeCap(cap: number) {
    if (!schema) return;
    const next = { ...schema.selfIntake, submissionsPerDayCap: cap };
    patchSchemaLocally((s) => ({ ...s, selfIntake: next }), { selfIntake: next });
  }
  function updateDefaultTtl(days: number | null) {
    patchSchemaLocally((s) => ({ ...s, defaultCodeTtlDays: days }), { defaultCodeTtlDays: days });
  }

  // ---------- Derived ----------

  const rulesFindings = useMemo(() => latestFindings(schema, 'rules_engine'), [schema]);
  const persistedAiFindings = useMemo(() => latestFindings(schema, 'ai_scan'), [schema]);
  const allAiFindings = aiFindings.length > 0 ? aiFindings : persistedAiFindings;

  const blockingFindings = useMemo(
    () =>
      [...rulesFindings, ...allAiFindings].filter((f) => f.severity === 'block'),
    [rulesFindings, allAiFindings],
  );

  const inlineWarning = useMemo(() => (schema ? inlineDisallowed(schema.properties) : null), [schema]);
  const requiredProperties = useMemo(
    () => (schema ? schema.properties.filter((p) => p.required && p.name) : []),
    [schema],
  );

  // Retention required if HIPAA or CAPTA framework is selected.
  const retentionRequired = useMemo(
    () =>
      schema?.complianceFrameworks.some((f) => f === 'HIPAA' || f === 'CAPTA') ?? false,
    [schema],
  );

  if (!industryId) {
    return <p style={{ color: 'var(--danger-color)' }}>Missing industry id.</p>;
  }

  // ----- Render new-schema bootstrap -----
  if (isNew) {
    return (
      <div>
        <div style={{ marginBottom: 16 }}>
          <Link
            to={`/industries/${industryId}/schemas`}
            style={{ color: 'var(--text-muted)', fontSize: 13, textDecoration: 'none' }}
          >
            ← Schemas
          </Link>
          <h1 style={{ margin: '4px 0 0' }}>New schema</h1>
          <p style={{ color: 'var(--text-muted)', fontSize: 13 }}>
            Create the schema, then add properties and configure compliance settings.
          </p>
        </div>
        {error && (
          <div className="form-error" style={{ marginBottom: 16 }}>
            {error}
          </div>
        )}
        <form onSubmit={createSchema} style={sectionStyle}>
          <div style={{ marginBottom: 12 }}>
            <label style={labelStyle}>Name *</label>
            <input
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              autoFocus
              style={inputStyle}
            />
          </div>
          <div style={{ marginBottom: 16 }}>
            <label style={labelStyle}>Description</label>
            <textarea
              value={newDescription}
              onChange={(e) => setNewDescription(e.target.value)}
              rows={3}
              style={inputStyle}
            />
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button type="submit" className="btn btn--primary" disabled={creating || !newName.trim()}>
              {creating ? 'Creating…' : 'Create schema'}
            </button>
            <button
              type="button"
              className="btn btn--ghost"
              onClick={() => navigate(`/industries/${industryId}/schemas`)}
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    );
  }

  if (loading || !schema) {
    return <p>Loading…</p>;
  }

  const isReadOnly = schema.status === 'revoked' || schema.status === 'archived';

  return (
    <div>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          marginBottom: 16,
          gap: 16,
          flexWrap: 'wrap',
        }}
      >
        <div style={{ flex: 1, minWidth: 0 }}>
          <Link
            to={`/industries/${industryId}/schemas`}
            style={{ color: 'var(--text-muted)', fontSize: 13, textDecoration: 'none' }}
          >
            ← Schemas
          </Link>
          <h1 style={{ margin: '4px 0 0', display: 'flex', alignItems: 'center', gap: 12 }}>
            {schema.name}
            <span style={{ fontSize: 14, color: 'var(--text-muted)', fontWeight: 400 }}>
              v{schema.version}
            </span>
            <StatusBadge status={schema.status} />
          </h1>
          {saving && (
            <p style={{ color: 'var(--text-muted)', fontSize: 12, margin: '4px 0 0' }}>Saving…</p>
          )}
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {schema.status === 'draft' && (
            <>
              <button
                type="button"
                className="btn btn--primary"
                onClick={() => void publish()}
                disabled={saving || blockingFindings.length > 0}
                title={blockingFindings.length > 0 ? 'Resolve blocking findings before publishing' : ''}
              >
                Publish
              </button>
              <button
                type="button"
                className="btn"
                onClick={() => setShowRevokeModal(true)}
                style={{ borderColor: 'var(--danger-color)', color: 'var(--danger-color)' }}
              >
                Revoke
              </button>
            </>
          )}
          {schema.status === 'published' && (
            <>
              <button type="button" className="btn" onClick={() => void archive()} disabled={saving}>
                Archive
              </button>
              <button
                type="button"
                className="btn"
                onClick={() => setShowRevokeModal(true)}
                style={{ borderColor: 'var(--danger-color)', color: 'var(--danger-color)' }}
              >
                Revoke
              </button>
            </>
          )}
          {schema.status === 'archived' && (
            <button
              type="button"
              className="btn"
              onClick={() => setShowRevokeModal(true)}
              style={{ borderColor: 'var(--danger-color)', color: 'var(--danger-color)' }}
            >
              Revoke
            </button>
          )}
          <button type="button" className="btn btn--ghost" onClick={() => setShowTestModal(true)}>
            Test this schema
          </button>
        </div>
      </div>

      {error && (
        <div className="form-error" style={{ marginBottom: 16 }}>
          {error}
        </div>
      )}

      {schema.status === 'revoked' && schema.revocationReason && (
        <div
          style={{
            padding: 12,
            background: 'rgba(239, 68, 68, 0.08)',
            border: '1px solid var(--danger-color)',
            borderRadius: 6,
            marginBottom: 16,
          }}
        >
          <strong>Revoked.</strong> {schema.revocationReason}
        </div>
      )}

      {blockingFindings.length > 0 && (
        <div
          style={{
            padding: 12,
            background: 'rgba(239, 68, 68, 0.08)',
            border: '1px solid var(--danger-color)',
            borderRadius: 6,
            marginBottom: 16,
          }}
        >
          <strong>Cannot publish.</strong> {blockingFindings.length} blocking finding
          {blockingFindings.length === 1 ? '' : 's'} below.
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 360px', gap: 16, alignItems: 'start' }}>
        <div>
          {/* Header section */}
          <section style={sectionStyle}>
            <h3 style={{ marginTop: 0 }}>Basics</h3>
            <div style={{ marginBottom: 12 }}>
              <label style={labelStyle}>Name *</label>
              <input
                value={schema.name}
                onChange={(e) => updateName(e.target.value)}
                style={inputStyle}
                disabled={isReadOnly}
              />
            </div>
            <div style={{ marginBottom: 0 }}>
              <label style={labelStyle}>Description</label>
              <textarea
                value={schema.description ?? ''}
                onChange={(e) => updateDescription(e.target.value)}
                rows={3}
                style={inputStyle}
                disabled={isReadOnly}
              />
            </div>
          </section>

          {/* Properties section */}
          <section style={sectionStyle}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <h3 style={{ margin: 0 }}>Properties</h3>
              <button
                type="button"
                className="btn btn--ghost"
                onClick={addProperty}
                disabled={isReadOnly}
              >
                + Add property
              </button>
            </div>
            {schema.properties.length === 0 ? (
              <p style={{ color: 'var(--text-muted)', fontSize: 14 }}>
                No properties yet. Add fields that your QR codes will capture.
              </p>
            ) : (
              <div style={{ display: 'grid', gap: 12 }}>
                {schema.properties.map((p, i) => (
                  <PropertyRow
                    key={i}
                    property={p}
                    index={i}
                    total={schema.properties.length}
                    isReadOnly={isReadOnly}
                    onChange={(patch) => updateProperty(i, patch)}
                    onRemove={() => removeProperty(i)}
                    onMove={(dir) => moveProperty(i, dir)}
                    allNames={schema.properties.map((q) => q.name)}
                  />
                ))}
              </div>
            )}
          </section>

          {/* Encoding & primary ID */}
          <section style={sectionStyle}>
            <h3 style={{ marginTop: 0 }}>Encoding & primary identifier</h3>
            <div style={{ marginBottom: 12 }}>
              <label style={labelStyle}>Encoding mode</label>
              <div style={{ display: 'flex', gap: 16 }}>
                <label
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                    opacity: inlineWarning ? 0.55 : 1,
                  }}
                  title={inlineWarning ?? 'Data is packed directly into the QR. Works offline.'}
                >
                  <input
                    type="radio"
                    name="encodingMode"
                    value="inline"
                    checked={schema.encodingMode === 'inline'}
                    onChange={() => updateEncodingMode('inline')}
                    disabled={isReadOnly || !!inlineWarning}
                  />
                  Inline
                </label>
                <label
                  style={{ display: 'flex', alignItems: 'center', gap: 6 }}
                  title="QR contains a signed pointer; data is fetched at scan time."
                >
                  <input
                    type="radio"
                    name="encodingMode"
                    value="reference"
                    checked={schema.encodingMode === 'reference'}
                    onChange={() => updateEncodingMode('reference')}
                    disabled={isReadOnly}
                  />
                  Reference
                </label>
              </div>
              <p style={{ color: 'var(--text-muted)', fontSize: 12, margin: '6px 0 0' }}>
                Inline = data in QR, no network required. Reference = signed pointer (smaller, but needs a scan-time
                lookup).
                {inlineWarning && (
                  <>
                    {' '}
                    <strong style={{ color: 'var(--warning-color, #d97706)' }}>{inlineWarning}</strong>
                  </>
                )}
              </p>
            </div>
            <div>
              <label style={labelStyle}>
                Primary identifier field
                {schema.selfIntake.enabled ? ' (optional)' : ' *'}
              </label>
              <select
                value={schema.primaryIdentifierField ?? ''}
                onChange={(e) => updatePrimaryIdentifier(e.target.value)}
                style={inputStyle}
                disabled={isReadOnly}
              >
                <option value="">— Select a required field —</option>
                {requiredProperties.map((p) => (
                  <option key={p.name} value={p.name}>
                    {p.name} ({p.label})
                  </option>
                ))}
              </select>
              <p style={{ color: 'var(--text-muted)', fontSize: 12, margin: '4px 0 0' }}>
                Must be one of the required properties. Used as the human-readable ID when self-intake is off.
              </p>
            </div>
            <div style={{ marginTop: 12 }}>
              <label style={labelStyle}>Default code TTL (days)</label>
              <input
                type="number"
                min={0}
                value={schema.defaultCodeTtlDays ?? ''}
                onChange={(e) =>
                  updateDefaultTtl(e.target.value === '' ? null : Math.max(0, Number(e.target.value)))
                }
                style={inputStyle}
                disabled={isReadOnly}
              />
              <p style={{ color: 'var(--text-muted)', fontSize: 12, margin: '4px 0 0' }}>
                Default expiration for codes generated against this schema. Leave blank for no default.
              </p>
            </div>
          </section>

          {/* Compliance */}
          <section style={sectionStyle}>
            <h3 style={{ marginTop: 0 }}>Compliance frameworks</h3>
            <FrameworkPicker
              selected={schema.complianceFrameworks}
              catalog={frameworks}
              disabled={isReadOnly}
              onChange={updateFrameworks}
            />
          </section>

          {/* Retention */}
          <section style={sectionStyle}>
            <RetentionEditor
              policy={schema.retentionPolicy}
              required={retentionRequired}
              disabled={isReadOnly}
              onChange={updateRetention}
            />
          </section>

          {/* Self-intake */}
          <section style={sectionStyle}>
            <h3 style={{ marginTop: 0 }}>Self-intake</h3>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 12 }}>
              <input
                type="checkbox"
                checked={schema.selfIntake.enabled}
                onChange={(e) => updateSelfIntakeEnabled(e.target.checked)}
                disabled={isReadOnly}
              />
              Enable self-intake link
            </label>
            {schema.selfIntake.enabled && (
              <>
                <div style={{ marginBottom: 12 }}>
                  <label style={labelStyle}>Submissions per day cap</label>
                  <input
                    type="number"
                    min={1}
                    max={intakeMaxPerDay}
                    value={schema.selfIntake.submissionsPerDayCap}
                    onChange={(e) =>
                      updateSelfIntakeCap(
                        Math.max(1, Math.min(intakeMaxPerDay, Number(e.target.value) || 1)),
                      )
                    }
                    style={inputStyle}
                    disabled={isReadOnly}
                  />
                  <p style={{ color: 'var(--text-muted)', fontSize: 12, margin: '4px 0 0' }}>
                    Max {intakeMaxPerDay} per day per token.
                  </p>
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center' }}>
                  {schema.selfIntake.tokenId && !schema.selfIntake.revokedAt ? (
                    <>
                      <span style={{ fontSize: 13 }}>
                        Active token. URL hidden — rotate to get a new copy.
                      </span>
                      <button
                        type="button"
                        className="btn"
                        onClick={() => void createOrRotateIntakeToken()}
                        disabled={isReadOnly || saving}
                      >
                        Rotate token
                      </button>
                      <button
                        type="button"
                        className="btn"
                        onClick={() => void revokeIntakeToken()}
                        disabled={isReadOnly || saving}
                        style={{ color: 'var(--danger-color)', borderColor: 'var(--danger-color)' }}
                      >
                        Revoke token
                      </button>
                    </>
                  ) : schema.selfIntake.revokedAt ? (
                    <>
                      <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>
                        Revoked on {new Date(schema.selfIntake.revokedAt).toLocaleString()}.
                      </span>
                      <button
                        type="button"
                        className="btn btn--primary"
                        onClick={() => void createOrRotateIntakeToken()}
                        disabled={isReadOnly || saving}
                      >
                        Rotate to re-enable
                      </button>
                    </>
                  ) : (
                    <button
                      type="button"
                      className="btn btn--primary"
                      onClick={() => void createOrRotateIntakeToken()}
                      disabled={isReadOnly || saving}
                    >
                      Create token
                    </button>
                  )}
                </div>
              </>
            )}
          </section>

          {/* Actions */}
          <section style={sectionStyle}>
            <h3 style={{ marginTop: 0 }}>Compliance scan</h3>
            <p style={{ color: 'var(--text-muted)', fontSize: 13, marginTop: 0 }}>
              Rules-based findings run on every save. Use the AI scan for an in-depth review of edge
              cases.
            </p>
            <button
              type="button"
              className="btn"
              onClick={() => void runAiScan()}
              disabled={saving || schema.status !== 'draft'}
            >
              Run AI compliance scan
            </button>
          </section>
        </div>

        {/* Compliance findings panel */}
        <aside>
          <CompliancePanel
            rulesFindings={rulesFindings}
            aiFindings={allAiFindings}
            rulesIndex={rules}
            frameworksCatalog={frameworks}
          />
        </aside>
      </div>

      {showRevokeModal && (
        <Modal
          title="Revoke schema"
          onClose={() => setShowRevokeModal(false)}
          footer={
            <>
              <button
                type="button"
                className="btn btn--ghost"
                onClick={() => setShowRevokeModal(false)}
                disabled={saving}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn"
                style={{ background: 'var(--danger-color)', color: '#fff', borderColor: 'var(--danger-color)' }}
                onClick={() => void confirmRevoke()}
                disabled={saving || !revokeReason.trim()}
              >
                Revoke
              </button>
            </>
          }
        >
          <p>
            Revoking a schema invalidates all codes generated against this version. This action is
            recorded in the audit log.
          </p>
          <label style={labelStyle}>Reason *</label>
          <textarea
            value={revokeReason}
            onChange={(e) => setRevokeReason(e.target.value)}
            rows={4}
            style={inputStyle}
            placeholder="Why are you revoking this schema?"
          />
        </Modal>
      )}

      {showTestModal && (
        <TestCodeModal
          schema={schema}
          industryId={industryId}
          onClose={() => setShowTestModal(false)}
        />
      )}

      {showIntakeUrlModal && (
        <Modal
          title="Self-intake link"
          onClose={() => setShowIntakeUrlModal(null)}
          footer={
            <button
              type="button"
              className="btn btn--primary"
              onClick={() => setShowIntakeUrlModal(null)}
            >
              Done
            </button>
          }
        >
          <p>
            <strong>Copy this URL now.</strong> It is shown only once; we keep just a hash on the
            server. If lost, rotate the token to get a new one.
          </p>
          <code
            style={{
              display: 'block',
              padding: 12,
              background: 'var(--bg-color, #f4f4f4)',
              borderRadius: 4,
              fontSize: 13,
              wordBreak: 'break-all',
            }}
          >
            {showIntakeUrlModal}
          </code>
          <button
            type="button"
            className="btn"
            style={{ marginTop: 8 }}
            onClick={() => void navigator.clipboard.writeText(showIntakeUrlModal)}
          >
            Copy to clipboard
          </button>
        </Modal>
      )}
    </div>
  );
}

// ============================ Sub-components ============================

function PropertyRow({
  property,
  index,
  total,
  isReadOnly,
  onChange,
  onRemove,
  onMove,
  allNames,
}: {
  property: SchemaProperty;
  index: number;
  total: number;
  isReadOnly: boolean;
  onChange: (patch: Partial<SchemaProperty>) => void;
  onRemove: () => void;
  onMove: (direction: -1 | 1) => void;
  allNames: string[];
}) {
  const nameInvalid = property.name && !SNAKE_CASE.test(property.name);
  const duplicate =
    property.name && allNames.filter((n) => n === property.name).length > 1;

  return (
    <div
      style={{
        border: '1px solid var(--border-color)',
        borderRadius: 6,
        padding: 12,
        display: 'grid',
        gap: 8,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: 'var(--text-muted)' }}>
        <span style={{ fontWeight: 600 }}>#{index + 1}</span>
        <button
          type="button"
          className="btn btn--ghost btn--small"
          onClick={() => onMove(-1)}
          disabled={isReadOnly || index === 0}
          style={{ padding: '2px 6px' }}
        >
          ↑
        </button>
        <button
          type="button"
          className="btn btn--ghost btn--small"
          onClick={() => onMove(1)}
          disabled={isReadOnly || index === total - 1}
          style={{ padding: '2px 6px' }}
        >
          ↓
        </button>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 140px', gap: 8 }}>
        <div>
          <label style={labelStyle}>Machine name *</label>
          <input
            value={property.name}
            onChange={(e) => onChange({ name: e.target.value })}
            placeholder="first_name"
            style={{ ...inputStyle, borderColor: nameInvalid || duplicate ? 'var(--danger-color)' : undefined }}
            disabled={isReadOnly}
          />
          {nameInvalid && (
            <p style={{ color: 'var(--danger-color)', fontSize: 11, margin: '4px 0 0' }}>
              Use snake_case (lowercase letters, numbers, underscores).
            </p>
          )}
          {duplicate && (
            <p style={{ color: 'var(--danger-color)', fontSize: 11, margin: '4px 0 0' }}>
              Duplicate name.
            </p>
          )}
        </div>
        <div>
          <label style={labelStyle}>Label *</label>
          <input
            value={property.label}
            onChange={(e) => onChange({ label: e.target.value })}
            placeholder="First Name"
            style={inputStyle}
            disabled={isReadOnly}
          />
        </div>
        <div>
          <label style={labelStyle}>Type</label>
          <select
            value={property.type}
            onChange={(e) => {
              const type = e.target.value as PropertyType;
              onChange({
                type,
                options: type === 'select' ? property.options ?? [] : null,
              });
            }}
            style={inputStyle}
            disabled={isReadOnly}
          >
            {PROPERTY_TYPES.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
        </div>
      </div>

      {property.type === 'select' && (
        <div>
          <label style={labelStyle}>Options (one per line) *</label>
          <textarea
            value={(property.options ?? []).join('\n')}
            onChange={(e) =>
              onChange({
                options: e.target.value
                  .split('\n')
                  .map((s) => s.trim())
                  .filter(Boolean),
              })
            }
            rows={3}
            style={inputStyle}
            disabled={isReadOnly}
          />
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
          <input
            type="checkbox"
            checked={property.required}
            onChange={(e) => onChange({ required: e.target.checked })}
            disabled={isReadOnly}
          />
          Required
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
          <input
            type="checkbox"
            checked={property.pii}
            onChange={(e) => onChange({ pii: e.target.checked })}
            disabled={isReadOnly}
          />
          PII
        </label>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
          <span>Sensitivity:</span>
          <select
            value={property.sensitiveLevel}
            onChange={(e) => onChange({ sensitiveLevel: e.target.value as SensitiveLevel })}
            style={{ ...inputStyle, padding: '0.25rem' }}
            disabled={isReadOnly}
          >
            {SENSITIVE_LEVELS.map((lvl) => (
              <option key={lvl} value={lvl}>
                {lvl}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <button
          type="button"
          className="btn btn--ghost"
          onClick={onRemove}
          disabled={isReadOnly}
          style={{ color: 'var(--danger-color)' }}
        >
          Remove
        </button>
      </div>
    </div>
  );
}

function FrameworkPicker({
  selected,
  catalog,
  disabled,
  onChange,
}: {
  selected: ComplianceFrameworkCode[];
  catalog: FrameworkConfig[];
  disabled: boolean;
  onChange: (next: ComplianceFrameworkCode[]) => void;
}) {
  const [adding, setAdding] = useState(false);
  const available = catalog.filter((f) => !selected.includes(f.code));
  return (
    <div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 8 }}>
        {selected.length === 0 && (
          <span style={{ color: 'var(--text-muted)', fontSize: 13 }}>No frameworks selected.</span>
        )}
        {selected.map((code) => {
          const meta = catalog.find((f) => f.code === code);
          return (
            <span
              key={code}
              title={meta?.description ?? ''}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                padding: '4px 8px',
                background: 'rgba(29, 78, 216, 0.1)',
                color: 'var(--primary-color)',
                borderRadius: 999,
                fontSize: 12,
                fontWeight: 600,
              }}
            >
              {code}
              {!disabled && (
                <button
                  type="button"
                  onClick={() => onChange(selected.filter((c) => c !== code))}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    color: 'inherit',
                    cursor: 'pointer',
                    fontSize: 14,
                    padding: 0,
                  }}
                >
                  ×
                </button>
              )}
            </span>
          );
        })}
      </div>
      {!disabled && available.length > 0 && (
        <>
          {adding ? (
            <select
              autoFocus
              defaultValue=""
              onChange={(e) => {
                const v = e.target.value;
                if (v) onChange([...selected, v as ComplianceFrameworkCode]);
                setAdding(false);
              }}
              onBlur={() => setAdding(false)}
              style={{ ...inputStyle, maxWidth: 320 }}
            >
              <option value="">— Pick framework —</option>
              {available.map((f) => (
                <option key={f.code} value={f.code}>
                  {f.code} — {f.name}
                </option>
              ))}
            </select>
          ) : (
            <button type="button" className="btn btn--ghost" onClick={() => setAdding(true)}>
              + Add framework
            </button>
          )}
        </>
      )}
    </div>
  );
}

function RetentionEditor({
  policy,
  required,
  disabled,
  onChange,
}: {
  policy: RetentionPolicy | null;
  required: boolean;
  disabled: boolean;
  onChange: (next: RetentionPolicy | null) => void;
}) {
  const enabled = policy !== null;
  return (
    <>
      <h3 style={{ marginTop: 0 }}>Retention policy</h3>
      <label style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 12 }}>
        <input
          type="checkbox"
          checked={enabled}
          onChange={(e) => {
            if (e.target.checked) {
              onChange({
                retainScansForDays: 365,
                retainParticipantRecordsForDays: null,
                deletionPolicy: 'soft',
                note: '',
              });
            } else if (!required) {
              onChange(null);
            }
          }}
          disabled={disabled || required}
        />
        Set retention policy
        {required && (
          <span style={{ color: 'var(--warning-color, #d97706)', fontSize: 12 }}>
            (required by selected frameworks)
          </span>
        )}
      </label>
      {policy && (
        <div style={{ display: 'grid', gap: 12 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}>
            <div>
              <label style={labelStyle}>Retain scans (days) *</label>
              <input
                type="number"
                min={0}
                value={policy.retainScansForDays}
                onChange={(e) =>
                  onChange({ ...policy, retainScansForDays: Math.max(0, Number(e.target.value) || 0) })
                }
                style={inputStyle}
                disabled={disabled}
              />
            </div>
            <div>
              <label style={labelStyle}>Retain participant records (days)</label>
              <input
                type="number"
                min={0}
                value={policy.retainParticipantRecordsForDays ?? ''}
                onChange={(e) =>
                  onChange({
                    ...policy,
                    retainParticipantRecordsForDays:
                      e.target.value === '' ? null : Math.max(0, Number(e.target.value)),
                  })
                }
                placeholder="(keep forever)"
                style={inputStyle}
                disabled={disabled}
              />
            </div>
            <div>
              <label style={labelStyle}>Deletion policy</label>
              <select
                value={policy.deletionPolicy}
                onChange={(e) =>
                  onChange({ ...policy, deletionPolicy: e.target.value as 'soft' | 'hard' })
                }
                style={inputStyle}
                disabled={disabled}
              >
                <option value="soft">Soft delete</option>
                <option value="hard">Hard delete</option>
              </select>
            </div>
          </div>
          <div>
            <label style={labelStyle}>Note</label>
            <textarea
              value={policy.note}
              onChange={(e) => onChange({ ...policy, note: e.target.value })}
              rows={2}
              style={inputStyle}
              disabled={disabled}
            />
          </div>
        </div>
      )}
    </>
  );
}

function CompliancePanel({
  rulesFindings,
  aiFindings,
  rulesIndex,
  frameworksCatalog,
}: {
  rulesFindings: ComplianceFinding[];
  aiFindings: ComplianceFinding[];
  rulesIndex: FrameworkRuleConfig[];
  frameworksCatalog: FrameworkConfig[];
}) {
  const merged = useMemo(
    () => [
      ...rulesFindings.map((f) => ({ ...f, _source: 'rules' as const })),
      ...aiFindings.map((f) => ({ ...f, _source: 'ai' as const })),
    ],
    [rulesFindings, aiFindings],
  );

  if (merged.length === 0) {
    return (
      <div
        style={{
          border: '1px solid var(--border-color)',
          borderRadius: 8,
          padding: 16,
          background: 'var(--card-bg)',
          position: 'sticky',
          top: 0,
        }}
      >
        <h3 style={{ marginTop: 0 }}>Compliance</h3>
        <p style={{ color: 'var(--text-muted)', fontSize: 13, margin: 0 }}>No findings. Schema is clean.</p>
      </div>
    );
  }

  return (
    <div
      style={{
        border: '1px solid var(--border-color)',
        borderRadius: 8,
        padding: 16,
        background: 'var(--card-bg)',
        position: 'sticky',
        top: 0,
      }}
    >
      <h3 style={{ marginTop: 0 }}>Compliance findings</h3>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {merged.map((f, i) => (
          <FindingCard
            key={i}
            finding={f}
            source={f._source}
            frameworksCatalog={frameworksCatalog}
            rulesIndex={rulesIndex}
          />
        ))}
      </div>
    </div>
  );
}

function FindingCard({
  finding,
  source,
  frameworksCatalog,
  rulesIndex,
}: {
  finding: ComplianceFinding;
  source: 'rules' | 'ai';
  frameworksCatalog: FrameworkConfig[];
  rulesIndex: FrameworkRuleConfig[];
}) {
  const colors = {
    block: { border: 'var(--danger-color)', bg: 'rgba(239, 68, 68, 0.08)', icon: '●', label: 'Block' },
    warn: { border: 'var(--warning-color, #d97706)', bg: 'rgba(217, 119, 6, 0.08)', icon: '▲', label: 'Warn' },
    info: { border: 'var(--primary-color)', bg: 'rgba(29, 78, 216, 0.08)', icon: '◆', label: 'Info' },
  }[finding.severity];

  const frameworkMeta = frameworksCatalog.find((f) => f.code === finding.framework);
  const ruleMeta = rulesIndex.find((r) => r.id === finding.ruleId);

  return (
    <div
      style={{
        border: `1px solid ${colors.border}`,
        background: colors.bg,
        borderRadius: 6,
        padding: 10,
        fontSize: 13,
      }}
      title={ruleMeta?.description ?? ''}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, marginBottom: 4 }}>
        <strong>
          <span style={{ color: colors.border, marginRight: 6 }}>{colors.icon}</span>
          {colors.label}
        </strong>
        <div style={{ display: 'flex', gap: 4 }}>
          {finding.framework && (
            <span
              title={frameworkMeta?.description ?? ''}
              style={{
                fontSize: 10,
                fontWeight: 600,
                padding: '1px 6px',
                borderRadius: 999,
                background: 'rgba(29, 78, 216, 0.1)',
                color: 'var(--primary-color)',
              }}
            >
              {finding.framework}
            </span>
          )}
          <span
            style={{
              fontSize: 10,
              fontWeight: 600,
              padding: '1px 6px',
              borderRadius: 999,
              background: 'var(--border-color)',
            }}
          >
            {source === 'ai' ? 'AI' : 'Rules'}
          </span>
        </div>
      </div>
      <div>{finding.message}</div>
      <div style={{ marginTop: 4, fontSize: 11, color: 'var(--text-muted)' }}>
        {finding.fieldName && <span>Field: {finding.fieldName}</span>}
        {finding.fieldName && finding.policySource && <span> · </span>}
        {finding.policySource && <span>{finding.policySource}</span>}
      </div>
    </div>
  );
}

// ============================ Modal ============================

function Modal({
  title,
  children,
  footer,
  onClose,
}: {
  title: string;
  children: React.ReactNode;
  footer?: React.ReactNode;
  onClose: () => void;
}) {
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{title}</h2>
          <button className="btn btn--ghost" aria-label="Close" onClick={onClose}>
            ×
          </button>
        </div>
        <div className="modal-body">{children}</div>
        {footer && <div className="modal-footer">{footer}</div>}
      </div>
    </div>
  );
}

// ============================ Test code modal ============================

function TestCodeModal({
  schema,
  industryId,
  onClose,
}: {
  schema: SchemaDoc;
  industryId: string;
  onClose: () => void;
}) {
  const [draft, setDraft] = useState<Record<string, string | number | boolean | null>>(() => {
    const d: Record<string, string | number | boolean | null> = {};
    for (const p of schema.properties) d[p.name] = p.type === 'boolean' ? false : '';
    return d;
  });
  const [result, setResult] = useState<TestCodeResponse | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function submit() {
    setBusy(true);
    setErr(null);
    try {
      const fields: Record<string, string | number | boolean | null> = {};
      for (const p of schema.properties) {
        const raw = draft[p.name];
        if (p.type === 'number') {
          fields[p.name] = raw === '' || raw === null || raw === undefined ? null : Number(raw);
        } else if (p.type === 'boolean') {
          fields[p.name] = Boolean(raw);
        } else {
          fields[p.name] = raw === '' || raw === undefined ? null : raw;
        }
      }
      const res = await apiCall<TestCodeResponse>(
        `/api/schemas/${schema.id}/test-code?industryId=${encodeURIComponent(industryId)}`,
        { method: 'POST', body: { fields } },
      );
      setResult(res);
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : (e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      title="Test this schema"
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn--ghost" onClick={onClose}>
            Close
          </button>
          {!result && (
            <button type="button" className="btn btn--primary" onClick={() => void submit()} disabled={busy}>
              {busy ? 'Generating…' : 'Generate test code'}
            </button>
          )}
        </>
      }
    >
      {err && <div className="form-error">{err}</div>}
      {!result ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <p style={{ fontSize: 13, color: 'var(--text-muted)' }}>
            Test codes expire after 24 hours and route to /test-decode when scanned. They are tagged
            as test-mode and excluded from production analytics.
          </p>
          {schema.properties.map((p) => (
            <TestFieldInput
              key={p.name}
              property={p}
              value={draft[p.name]}
              onChange={(v) => setDraft((d) => ({ ...d, [p.name]: v }))}
            />
          ))}
        </div>
      ) : (
        <div style={{ textAlign: 'center' }}>
          <p>
            <span
              className="badge"
              style={{
                background: 'var(--warning-color, #d97706)',
                color: '#fff',
              }}
            >
              Test code · 24h
            </span>
          </p>
          <img
            src={result.dataUri}
            alt="Test QR"
            width={280}
            height={280}
            style={{ background: '#fff', padding: 8, borderRadius: 4, imageRendering: 'pixelated' }}
          />
          {result.expiresAt && (
            <p style={{ color: 'var(--text-muted)', fontSize: 12 }}>
              Expires {new Date(result.expiresAt).toLocaleString()}
            </p>
          )}
        </div>
      )}
    </Modal>
  );
}

function TestFieldInput({
  property,
  value,
  onChange,
}: {
  property: SchemaProperty;
  value: string | number | boolean | null | undefined;
  onChange: (v: string | number | boolean | null) => void;
}) {
  const id = `test-${property.name}`;
  return (
    <div>
      <label htmlFor={id} style={labelStyle}>
        {property.label}
        {property.required ? ' *' : ''}
      </label>
      {property.type === 'select' ? (
        <select
          id={id}
          value={value === null || value === undefined ? '' : String(value)}
          onChange={(e) => onChange(e.target.value)}
          style={inputStyle}
        >
          <option value="">— Select —</option>
          {(property.options ?? []).map((opt) => (
            <option key={opt} value={opt}>
              {opt}
            </option>
          ))}
        </select>
      ) : property.type === 'boolean' ? (
        <input
          id={id}
          type="checkbox"
          checked={Boolean(value)}
          onChange={(e) => onChange(e.target.checked)}
        />
      ) : property.type === 'number' ? (
        <input
          id={id}
          type="number"
          value={value === null || value === undefined ? '' : String(value)}
          onChange={(e) => onChange(e.target.value)}
          style={inputStyle}
        />
      ) : property.type === 'date' ? (
        <input
          id={id}
          type="date"
          value={value === null || value === undefined ? '' : String(value)}
          onChange={(e) => onChange(e.target.value)}
          style={inputStyle}
        />
      ) : (
        <input
          id={id}
          type="text"
          value={value === null || value === undefined ? '' : String(value)}
          onChange={(e) => onChange(e.target.value)}
          style={inputStyle}
        />
      )}
    </div>
  );
}
